import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

export type PriceUpdateDirection = "increase" | "decrease";
export type PriceUpdateCurrency = "EUR" | "USD";

export interface PriceUpdateLastInfo {
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
  lastPercentage: number | null;
  lastDirection: PriceUpdateDirection | null;
}

export interface PriceUpdateSummary {
  totalRecords: number;
  eur: PriceUpdateLastInfo;
  usd: PriceUpdateLastInfo;
}

export interface PriceUpdateRequest {
  percentage: number;
  direction: PriceUpdateDirection;
}

export interface PriceUpdateResponse {
  success: true;
  currency: PriceUpdateCurrency;
  direction: PriceUpdateDirection;
  percentage: number;
  recordsUpdated: number;
  updatedAt: string;
  updatedBy: string;
  formula: string;
}

export async function getPriceUpdateSummary(): Promise<PriceUpdateSummary> {
  const res = await Api.get("/v1/admin/price-update/summary");
  return unwrap<PriceUpdateSummary>(res.data);
}

export async function applyPriceUpdate(
  currency: PriceUpdateCurrency,
  body: PriceUpdateRequest,
): Promise<PriceUpdateResponse> {
  const path = currency === "EUR" ? "eur" : "usd";
  const res = await Api.post(`/v1/admin/price-update/${path}`, body);
  return unwrap<PriceUpdateResponse>(res.data);
}

// ---------------------------------------------------------------------------
// Dataset-scoped bulk price change ("% change bulk price")
//
// Same ± % semantics as the PEMSERTER routes above, targeted at a dataset that
// svc-04 registered as bulk-updatable. History for every dataset lands in the
// same `penn_config.price_update_log` table, discriminated by dataset + region.
// ---------------------------------------------------------------------------

/** Wire keys — must match PRICE_UPDATE_DATASETS in svc-04. */
export type PriceUpdateDataset =
  | "pemserter-price-list"
  | "profil-indie-options"
  | "sherex-options"
  | "autoriv-configurator-items";

export interface DatasetPriceUpdateSummary {
  dataset: PriceUpdateDataset;
  regionId: number | null;
  currency: PriceUpdateCurrency;
  /** Price values the change will touch. */
  totalRecords: number;
  last: PriceUpdateLastInfo;
}

export interface DatasetPriceUpdateRequest {
  percentage: number;
  direction: PriceUpdateDirection;
  /** Required for region-scoped datasets. */
  regionId?: number;
}

export interface DatasetPriceUpdateResponse {
  success: true;
  dataset: PriceUpdateDataset;
  regionId: number | null;
  currency: PriceUpdateCurrency;
  direction: PriceUpdateDirection;
  percentage: number;
  recordsUpdated: number;
  updatedAt: string;
  updatedBy: string;
  formula: string;
}

export async function getDatasetPriceUpdateSummary(
  dataset: PriceUpdateDataset,
  regionId?: number,
): Promise<DatasetPriceUpdateSummary> {
  const res = await Api.get(`/v1/admin/price-update/datasets/${dataset}/summary`, {
    params: regionId === undefined ? undefined : { regionId },
  });
  return unwrap<DatasetPriceUpdateSummary>(res.data);
}

export async function applyDatasetPriceUpdate(
  dataset: PriceUpdateDataset,
  body: DatasetPriceUpdateRequest,
): Promise<DatasetPriceUpdateResponse> {
  const res = await Api.post(`/v1/admin/price-update/datasets/${dataset}`, body);
  return unwrap<DatasetPriceUpdateResponse>(res.data);
}
